import { Download, Banknote, CreditCard, Smartphone, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@akuna/ui";
import type { PaymentMethodRow } from "@/hooks/useRevenueReport";
import { exportToExcel } from "@/lib/utils/export";

interface RevenuePaymentMethodsProps {
  data: PaymentMethodRow[];
}

function methodIcon(method: string) {
  if (method === "PIX") return <Smartphone className="h-4 w-4 shrink-0" />;
  if (method === "CARTAO_CREDITO" || method === "CARTAO_DEBITO")
    return <CreditCard className="h-4 w-4 shrink-0" />;
  if (method === "A_RECEBER") return <Clock className="h-4 w-4 shrink-0" />;
  return <Banknote className="h-4 w-4 shrink-0" />;
}

const METHOD_COLORS: Record<string, string> = {
  PIX: "bg-green-500",
  CARTAO_CREDITO: "bg-blue-500",
  CARTAO_DEBITO: "bg-indigo-500",
  DINHEIRO: "bg-amber-500",
  A_RECEBER: "bg-orange-400",
};

function formatCurrency(value: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
}

export function RevenuePaymentMethods({ data }: RevenuePaymentMethodsProps) {
  const { t } = useTranslation("reports");

  function handleExport() {
    exportToExcel({
      rows: data.map((r) => ({
        [t("paymentMethod")]: t(`paymentMethodLabel.${r.method}`, { defaultValue: r.method }),
        [t("amount")]: r.amount,
        [t("percentage")]: `${r.percentage.toFixed(1)}%`,
      })),
      sheetName: t("paymentMethodsTitle"),
      fileName: "formas-de-pagamento.xlsx",
    });
  }

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="text-base">{t("paymentMethodsTitle")}</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={data.length === 0}
          >
            <Download className="h-4 w-4 mr-1" />
            {t("export")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 flex-1">
        {data.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">{t("emptyBreakdown")}</p>
        ) : (
          data.map((row) => (
            <div key={row.method} className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  {methodIcon(row.method)}
                  <span className="text-sm font-medium text-foreground">
                    {t(`paymentMethodLabel.${row.method}`, { defaultValue: row.method })}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold">{formatCurrency(row.amount)}</span>
                  <span className="text-xs text-muted-foreground w-10 text-right">
                    {row.percentage.toFixed(1)}%
                  </span>
                </div>
              </div>
              <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className={`h-full rounded-full ${METHOD_COLORS[row.method] ?? "bg-primary"}`}
                  style={{ width: `${Math.max(row.percentage, 2)}%` }}
                />
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
